// @mui
import { styled } from '@mui/material/styles';
import {
  ListItemText,
  ListItemIcon,
  ListSubheader,
  ListItemButton,
  ListItemTextProps,
  ListItemButtonProps,
} from '@mui/material';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { ICON, NAVBAR } from '../../../config';

// ----------------------------------------------------------------------

export interface ListItemStyleProps extends ListItemButtonProps {
  active: boolean;
  depth: number;
  isCollapse?: boolean;
}

export const ListItemStyle = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'isCollapse',
})<ListItemStyleProps>(({ active, depth, isCollapse, theme }) => ({
  position: 'relative',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1.5),
  color: theme.palette.text.primary,
  borderRadius: 0,
  '&:hover .MuiTypography-root': {
    fontFamily: 'ObjektivMk1Md !important',
    fontWeight: '600',
  },
  '&:hover .MuiListItemIcon-root': {
    transform: 'scale(1.1)',
  },
  ...(depth && depth > 1 ? {
    height: 'auto',
    paddingTop: '0',
    paddingBottom: '0',
    marginBottom: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    gap: 0,
    ...(active && {
      '& .MuiTypography-root': {
        color: theme.palette.primary.main,
      },
    }),
  } : {
    height: NAVBAR.DASHBOARD_ITEM_ROOT_HEIGHT,
    marginBottom: theme.spacing(0.5),
    gap: theme.spacing(1),
    ...(active && {
      '&.MuiListItemButton-root': {
        backgroundColor: theme.palette.primary.lighter,
      },
    }),
  }),
  listStyle: 'none',
  '& .MuiListItemIcon-root': {
    margin: isCollapse ? '0 auto' : '0'
  },
  // Sub item
  ...(depth && {
    ...(depth > 1 && {
      maxHeight: NAVBAR.DASHBOARD_ITEM_ROOT_HEIGHT,
    }),
    ...(depth > 2 && {
      paddingLeft: theme.spacing(depth),
    }),
  }),
}));

// ----------------------------------------------------------------------

interface ListItemTextStyleProps extends ListItemTextProps {
  isCollapse?: boolean;
  isChild?: boolean;
}

export const ListItemTextStyle = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isChild',
})<ListItemTextStyleProps>(({ isCollapse, isChild, theme }) => ({
  transition: theme.transitions.create(['width', 'opacity'], {
    duration: theme.transitions.duration.shorter,
  }),
  flex: 'initial',
  ...(isCollapse && {
    width: 0,
    opacity: 0,
  }),
  ...(isChild && {
    borderLeft: `solid 1px ${theme.palette.grey["300"]}`,
    padding: `${theme.spacing(0.5)} ${theme.spacing(0)} ${theme.spacing(0.5)} ${theme.spacing(2)}`,
    position: 'relative',
    '&.list-item-active-pointer:before': {
      content:'""',
      width: 5,
      height: 5,
      background: theme.palette.primary.main,
      left: '-3px',
      top: 'calc(50% - 1px)',
      position: 'absolute'
    }
  })
}));

// ----------------------------------------------------------------------

export const ListItemIconStyle = styled(ListItemIcon)({
  width: ICON.NAVBAR_ITEM,
  height: ICON.NAVBAR_ITEM,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': { width: '100%', height: '100%' },
});

// ----------------------------------------------------------------------

export const ListSubheaderStyle = styled(ListSubheader)(({ theme }) => ({
  ...theme.typography.overline,
  borderRadius: theme.shape.borderRadius,
  paddingTop: theme.spacing(3),
  paddingLeft: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  color: theme.palette.text.primary,
  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration.shorter,
  }),
  ...cssStyles(theme).bgBlur(),
}));
